<script setup lang="ts">
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Dialog } from '@capacitor/dialog'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { SafeArea } from '@capacitor-community/safe-area'

import refreshAll from './services/refreshHelper'

const colorMode = useColorMode()

SafeArea.enable({
  config: {
    customColorsForSystemBars: true,
    statusBarColor: colorMode.value === 'dark' ? '#000000' : '#ffffff',
    statusBarContent: colorMode.value === 'dark' ? 'light' : 'dark',
    navigationBarColor: colorMode.value === 'dark' ? '#000000' : '#ffffff',
    navigationBarContent: colorMode.value === 'dark' ? 'light' : 'dark',
    offset: 0,
  },
})

if (Capacitor.isNativePlatform()) {
  CapacitorUpdater.addListener('updateAvailable', async (res) => {
    try {
      const { value } = await Dialog.confirm({
        title: 'Update Available',
        message: `Version ${res.bundle.version} is available. Would you like to update now?`,
      })

      if (value) {
        CapacitorUpdater.set(res.bundle)
      }
    }
    catch (error) {
      // @todo: Report this on sentry?
      console.log(error)
    }
  })

  CapacitorUpdater.notifyAppReady()

  App.addListener('resume', async () => {
    refreshAll(() => { })
  })
}

const route = useRoute()

const isTablet = route.path.startsWith('/tablet') || route.path.startsWith('/competition')

const links = [
  { rel: 'icon', href: '/favicon.ico' },
]

if (isTablet) {
  links.push({
    rel: 'manifest',
    href: '/scoring-manifest.json',
  })
}

useHead({
  titleTemplate: '%s - DartsSpace | Tournament platform',
  meta: [
    { charset: 'utf-8' },
    { name: 'HandheldFriendly', content: 'true' },
    { key: 'theme-color', name: 'theme-color', content: '#111827' },
  ],
  script: [],
  link: links,
  htmlAttrs: {
    lang: 'en',
  },
})

const title = 'DartsSpace'
const description = 'Next-level darts tournament platform'

const head = useLocaleHead({
  addDirAttribute: true,
  addSeoAttributes: true,
  baseUrl: 'https://dartsspace.com',
})

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
})

const maximize = ref(false)
const toggleMaximize = () => {
  maximize.value = !maximize.value
}
provide('toggleMaximize', { maximize, toggleMaximize })

defineOgImageComponent('DartsSpace')

onMounted(() => {
  App.addListener('backButton', () => {
    if (window.history.length > 1) {
      window.history.back()
    }
  })
})

const authStore = useAuthStore()
await callOnce('loadUser', () => authStore.loadUser())
</script>

<template>
  <div>
    <Html
      :lang="head.htmlAttrs?.lang"
      :dir="head.htmlAttrs?.dir"
    >
      <Head>
        <template
          v-for="link in head.link"
          :key="link.id"
        >
          <Link
            :id="link.id"
            :rel="link.rel"
            :href="link.href"
            :hreflang="link.hreflang"
          />
        </template>
        <meta
          name="viewport"
          content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
        >
        <template
          v-for="meta in head.meta"
          :key="meta.id"
        >
          <Meta
            :id="meta.id"
            :property="meta.property"
            :content="meta.content"
          />
        </template>
      </Head>

      <Body class="overflow-hidden">
        <NuxtLoadingIndicator />

        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>

        <ClientOnly>
          <UiToaster />
          <OverlayProvider />
          <FeatureAnnouncementOverlay v-if="authStore.user" />
        </ClientOnly>
      </Body>
    </Html>
  </div>
</template>
